import { useEffect, useState } from 'react'
import { IInfrastructureProject, IInfrastructureProjectObject, InfrastructureProjectType } from '../../../../types/project.types'
import classes from './styles.module.scss'
import generalClasses from '../styles.module.scss'
import { useTranslation } from 'react-i18next';
import Field from '../../../../components/field/field.component';
import { findOptionByValue, isEmpty, t } from '../../../../helpers/utils';
import ReactSelect from 'react-select';
import Documentation from '../../components/documentation.component';
import AdditionalInfo from '../../components/additional-info.component';
import FooterComponent from '../../components/footer.component';
import ImageGrid from '../../components/image-grid.component';
import { useParams } from 'react-router-dom';
import { updateInfrastructureProject } from '../../../../requests/supervisor.request';
import { toast } from 'react-toastify';
import { conditionOptions, operatorOptions, yesOrNo } from '../../../../consts/project.const';

interface IProps {
  data: IInfrastructureProjectObject
  loadData: () => void;
}

const baseClass = 'internet-tab';
const translationKey = 'projects.infrastructure-projects.internet';

const keys: Array<keyof IInfrastructureProject> = [
  'factCondition', 'repairRequired'
]

const InternetTab = ({ data, loadData }: IProps) => {
  const { tab } = useParams<{ tab: string }>();
  const { i18n: { language } } = useTranslation();
  const [formData, setFormData] = useState<IInfrastructureProjectObject>(data);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const { kato } = useParams<{ kato: string }>();

  const [isAddingFund, setIsAddingFund] = useState(false);
  const [newFund, setNewFund] = useState<{ labelRu: string; labelKz: string; value: string }>({ labelRu: '', labelKz: '', value: '' });

  const [isAddingInfo, setIsAddingInfo] = useState(false);
  const [newInfo, setNewInfo] = useState<{ labelRu: string; labelKz: string; valueRu: string; valueKz: string }>({ labelRu: '', labelKz: '', valueRu: '', valueKz: '' });

  const [isAddingText, setIsAddingText] = useState(false);
  const [newText, setNewText] = useState({
    textRu: '',
    textKz: ''
  });

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      infrastructureEntity: {
        ...prev.infrastructureEntity,
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    if (validateFields()) {
      const original = formData.infrastructureEntity;
      const other = original.otherProviders?.trim();
      const currentProviders = original.communicationProvider?.name || '';
      const existingList = currentProviders.split(',').map(p => p.trim()).filter(Boolean);

      if (other && !existingList.includes(other) && existingList.includes('other')) {
        existingList.push(other);
      }

      const body: IInfrastructureProject = {
        ...original,
        communicationProvider: {
          ...original.communicationProvider,
          name: existingList.join(', ')
        }
      };

      const { otherProviders, ...rest } = body;

      updateInfrastructureProject(tab as InfrastructureProjectType, rest as IInfrastructureProject).then(() => {
        loadData();
        toast.success(t(`admin.success`, language));
      });
    }
  };

  const validateFields = () => {
    setErrors({});
    const newErrors: Record<string, boolean> = {};
    const { infrastructureEntity } = formData;

    keys.forEach(key => {
      if (isEmpty(infrastructureEntity[key])) {
        newErrors[key] = true;
      }
    });

    if (isEmpty(infrastructureEntity.communicationProvider?.name) || isEmpty(infrastructureEntity.communicationProvider)) {
      newErrors['communicationProvider'] = true;
    }

    if (infrastructureEntity.communicationProvider?.name?.includes('other') && isEmpty(infrastructureEntity.otherProviders)) {
      newErrors['otherProviders'] = true;
    }

    ['subscriberNumber', 'factCondition', 'repairRequired'].forEach(key => {
      if (isEmpty(infrastructureEntity[key as keyof IInfrastructureProject])) {
        newErrors[key] = true;
      }
    });

    if (infrastructureEntity.documentationStatus === 'HAVE' && infrastructureEntity.repairRequired?.toString() === 'true') {
      ['workStart', 'workEnd', 'requiredFundSourceRu', 'requiredFundSourceKz', 'requiredFundAmount'].forEach(key => {
        if (isEmpty(infrastructureEntity[key as keyof IInfrastructureProject])) {
          newErrors[key] = true;
        }
      })

      infrastructureEntity.requiredFund && Object.entries(infrastructureEntity.requiredFund).forEach(([key, value]) => {
        if (isEmpty(value.value)) {
          newErrors[`requiredFund.${key}-value`] = true;
        }
      });
    }

    infrastructureEntity.additionalFields && Object.entries(infrastructureEntity.additionalFields).forEach(([key, value]) => {
      if (key.includes('text')) {
        newErrors[`additionalFields.${key}.textRu`] = isEmpty(value.textRu);
        newErrors[`additionalFields.${key}.textKz`] = isEmpty(value.textKz);
      } else {
        newErrors[`additionalFields.${key}.valueRu`] = isEmpty(value.valueRu);
        newErrors[`additionalFields.${key}.valueKz`] = isEmpty(value.valueKz);
      }
    });

    // if (!infrastructureEntity.files?.length) {
    //   toast.error('Загрузите фото');
    //   newErrors['files'] = true;
    // }

    setErrors(newErrors);

    return Object.values(newErrors).every(error => !error);
  }

  useEffect(() => {
    if (!data) return;
    setFormData(data);
  }, [data]);

  const renderFields = (lang: 'ru' | 'kz') => {
    const { infrastructureEntity } = formData;

    return <div className={generalClasses[`fields-container`]}>

      <div className={`${generalClasses[`field-block`]}`}>
        <div className={`${generalClasses[`field-block__label`]}`}>
          {t(`${translationKey}.communicationProvider`, lang)}
        </div>

        {(() => {
          const providerName = formData.infrastructureEntity.communicationProvider?.name || '';
          const selectedValues = providerName
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

          const options = operatorOptions(t, translationKey, lang);
          const knownOptionValues = options.map(o => o.value);

          const knownSelected = selectedValues.filter(val =>
            knownOptionValues.some(known => known.toLowerCase() === val.toLowerCase())
          );

          const unknownSelected = selectedValues.filter(val =>
            !knownOptionValues.some(known => known.toLowerCase() === val.toLowerCase())
          );

          const includesOther = knownSelected.some(v => v.toLowerCase() === 'other');

          if (includesOther && unknownSelected.length) {
            const unknownCombined = unknownSelected.join(', ');
            if (formData.infrastructureEntity.otherProviders !== unknownCombined) {
              setFormData(prev => ({
                ...prev,
                infrastructureEntity: {
                  ...prev.infrastructureEntity,
                  otherProviders: unknownCombined
                }
              }));
            }
          }

          const combinedSelectedOptions = options.filter(opt =>
            knownSelected.some(v => v.toLowerCase() === opt.value.toLowerCase())
          );

          return (
            <ReactSelect
              isMulti
              options={options}
              value={combinedSelectedOptions}
              onChange={(selectedOptions) => {
                const values = (selectedOptions || []).map(opt => opt.value);
                const newOtherProviders =
                  values.some(v => v.toLowerCase() === 'other')
                    ? selectedValues.filter(
                      val => !knownOptionValues.some(opt => opt.toLowerCase() === val.toLowerCase())
                    ).join(', ')
                    : '';

                setFormData(prev => ({
                  ...prev,
                  infrastructureEntity: {
                    ...prev.infrastructureEntity,
                    communicationProvider: {
                      name: values.join(', ')
                    },
                    otherProviders: newOtherProviders
                  }
                }));
              }}
              placeholder=""
              className={errors[`communicationProvider`] ? generalClasses[`error-select`] : ''}
            />
          );
        })()}

        {formData.infrastructureEntity?.communicationProvider?.name?.toLowerCase().includes('other') && (
          <Field
            key={`otherProviders`}
            label={t(`${translationKey}.otherProviders`, lang)}
            value={formData.infrastructureEntity?.otherProviders || ''}
            onChange={(value) => {
              handleInputChange(`otherProviders`, value);
            }}
            type="text"
            required
            error={errors[`otherProviders`]}
          />
        )}

        <Field
          key={`subscriberNumber`}
          label={t(`${translationKey}.subscriberNumber`, lang)}
          value={formData.infrastructureEntity?.subscriberNumber || ''}
          onChange={(value) => {
            handleInputChange(`subscriberNumber`, value);
          }}
          type="number"
          required
          error={errors[`subscriberNumber`]}
        />
      </div>


      <div className={`${generalClasses[`field-block`]} ${generalClasses[`field-block--gray`]}`}>
        <div
          className={`${generalClasses['field-block__label']} ${generalClasses['required']}`}>
          {t(`${translationKey}.factCondition`, lang)}
        </div>
        <ReactSelect
          options={conditionOptions(t, 'projects.infrastructure-projects', lang)}
          value={findOptionByValue(infrastructureEntity?.factCondition, conditionOptions(t, 'projects.infrastructure-projects', lang))}
          onChange={(option) => handleInputChange('factCondition', option?.value || '')}
          placeholder=""
          className={errors[`factCondition`] ? generalClasses[`error-select`] : ''}
        />
      </div>

      <div className={`${generalClasses[`field-block`]} ${generalClasses[`field-block--gray`]}`}>
        <div
          className={`${generalClasses['field-block__label']} ${generalClasses['required']}`}>
          {t(`${translationKey}.repairRequired`, lang)}
        </div>
        <ReactSelect
          options={yesOrNo(t, 'projects.infrastructure-projects', lang)}
          value={findOptionByValue(infrastructureEntity?.repairRequired, yesOrNo(t, 'projects.infrastructure-projects', lang))}
          onChange={(option) => handleInputChange('repairRequired', option?.value || '')}
          placeholder=""
          className={errors[`repairRequired`] ? generalClasses[`error-select`] : ''}
        />
      </div>

      {infrastructureEntity?.repairRequired?.toString() === 'true' &&
        <Documentation
          data={formData}
          setData={setFormData}
          lang={lang}
          newFund={newFund}
          setNewFund={setNewFund}
          isAddingFund={isAddingFund}
          setIsAddingFund={setIsAddingFund}
          parentErrors={errors}
        />}

      <AdditionalInfo
        data={formData}
        setData={setFormData}
        lang={lang}
        newText={newText}
        setNewText={setNewText}
        newInfo={newInfo}
        setNewInfo={setNewInfo}
        isAddingText={isAddingText}
        setIsAddingText={setIsAddingText}
        isAddingInfo={isAddingInfo}
        setIsAddingInfo={setIsAddingInfo}
        parentErrors={errors}
      />
    </div>

  }

  if (!data || !formData) return null;
  return (
    kato && <div className={classes[baseClass]}>
      <div className={generalClasses['grid']}>
        {renderFields(language === 'ru' ? 'ru' : 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}
      </div>
      <ImageGrid
        kato={+kato}
        imageData={formData.images}
        type={'infrastructure-projects'}
        objectId={formData.infrastructureEntity.id}
        setFormData={setFormData}
      />
      <FooterComponent
        onSave={handleSave}
        validateFields={validateFields}
      />
    </div>
  )
}

export default InternetTab